import type { Branded } from "@@/utilities/branded";
import { z } from "zod";

type Base64 = Branded<string, "base64">;
// Converts a UTF-8 string to a Base64-encoded string
const utf8ToBase64 = (value: string): Base64 => {
    const utf8Bytes = new TextEncoder().encode(value);
    const binaryString = Array.from(utf8Bytes)
        .map(byte => String.fromCharCode(byte))
        .join("");
    return btoa(binaryString) as Base64;
};

// Converts a Base64-encoded string back to a UTF-8 string
const base64ToUtf8 = (base64: Base64): string => {
    const binaryString = atob(base64);
    const utf8Bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));
    return new TextDecoder().decode(utf8Bytes);
};

const base64ZodString = z.string() as unknown as z.ZodSchema<Base64>;

const dataUriToBlobData = (
    dataUri: Base64,
): {
    data: Base64;
    mimeType: string;
} => {
    const dataUriSplit = dataUri.split(",");
    return {
        data: dataUriSplit[1] as Base64,
        mimeType: dataUriSplit[0].split(":")[1].split(";")[0],
    };
};

const fileToDataUri = async (file: File): Promise<Base64> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as Base64);
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reader.onerror = error => reject(error);
        reader.onabort = _ => reject(new Error("cancelled"));
    });

export {
    base64ToUtf8,
    base64ZodString,
    dataUriToBlobData,
    fileToDataUri,
    utf8ToBase64,
};
export type { Base64 };
